import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {ListChildComponentProps, VariableSizeList} from 'react-window';

import * as analytics from 'analytics/Analytics';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import AnnotationTypeVisibilityDropdown from 'molbio/annotations/AnnotationTypeVisibilityDropdown';
import {AnnotationData, getUniqueNonEmptyAnnotationTypes} from 'molbio/annotations/AnnotationUtil';
import {BenchlingDispatch} from 'redux-stores/Store';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  cancelCreateNewAnnotation,
  openCreateNewAnnotation,
} from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import FeatureRowItem, {BASE_FEATURE_ROW_HEIGHT} from 'toolbar/feature-panel/FeatureRowItem';
import {useAnnotationPanelUIBySequence} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';

import {SaveAnnotationData} from './AnnotationPanelUtil';
import CreateEditAnnotationForm from './CreateEditAnnotationForm';

export interface AnnotationPanelContentProps {
  sortedAnnotationsData: Array<AnnotationData>;
  canEditSequenceFeatures: boolean;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  isSingleStranded: boolean;
  hasDuplicateAnnotations: boolean;
  onClickAutoAnnotate: () => void;
  onToggleVisibility: (ids: Array<string>, visible: boolean) => void;
  onAnnotationHover?: (id: string, hover: boolean) => void;
  onSelectAnnotation: (id: string, selected: boolean) => void;
  onSaveAnnotation: (id: string | null, data: SaveAnnotationData) => void;
  onDeleteAnnotation: (id: string) => void;
  onOpenCleanDuplicates: () => void;
  openExpandedAnnotations: () => void;
  className?: string;
}

interface AnnotationPanelRowVirtualizedListData {
  sortedAnnotationsData: Array<AnnotationData>;
  canEditSequenceFeatures: boolean;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  isSingleStranded: boolean;
  onToggleVisibility: (ids: Array<string>, visible: boolean) => void;
  onAnnotationHover?: (id: string, hover: boolean) => void;
  onSelectAnnotation: (id: string, selected: boolean) => void;
  onSaveAnnotation: (id: string | null, data: SaveAnnotationData) => void;
  onDeleteAnnotation: (id: string) => void;
  openExpandedAnnotations: () => void;
  allAnnotationTypes: Array<string>;
  allCustomFieldKeys: Array<string>;
}

// Heights of various elements in each row used to calculate the row height for virtualization
const EDIT_FORM_BASE_FIELD_HEIGHT = 43;
const NUM_EDIT_FIELDS = 5;
const EMPTY_NOTES_FIELD_HEIGHT = 50;
const NOTES_FIELD_HEIGHT = 80;
const CUSTOM_FIELD_BASE_HEIGHT = 35;
const CUSTOM_FIELD_ROW_HEIGHT = 44;
const EDIT_FORM_BUTTONS_HEIGHT = 45;

/**
 * Renders a single annotation row based on the props provided by the virtualized list.
 *
 * Note: This function needs to be defined at the top level (instead of inline in the panel component as
 * a closure). Otherwise, the edit form will lose focus on each update.
 * See: https://github.com/bvaughn/react-window/issues/420
 */
function AnnotationPanelRow({
  index,
  data,
  style,
}: ListChildComponentProps<AnnotationPanelRowVirtualizedListData>): JSX.Element {
  const annotation = data.sortedAnnotationsData[index];
  const canEdit = data.canEditSequenceFeatures && annotation.editable;
  // Update the computed style to cap the height and add a scrollbar.
  const updatedStyle = {
    ...style,
    maxHeight: style.height,
    overflow: 'auto',
  };
  const contextMenuOptions = [
    {
      name: 'Remove annotation',
      click: () => {
        data.onDeleteAnnotation(annotation.cid);
      },
      hide: !canEdit,
    },
    {
      name: 'Expand annotation',
      click: () => {
        data.openExpandedAnnotations();
      },
    },
  ];

  return (
    <div style={updatedStyle}>
      <FeatureRowItem
        key={annotation.cid}
        feature={annotation}
        getLocationString={(anno) => `${anno.displayStart}-${anno.displayEnd}`}
        onToggleVisibility={data.onToggleVisibility}
        onHover={data.onAnnotationHover}
        onSelect={data.onSelectAnnotation}
        editForm={
          <CreateEditAnnotationForm
            initialAnnotationData={annotation}
            onClickSave={(saveData) => {
              data.onSaveAnnotation(annotation.cid, saveData);
            }}
            onClickCancel={() => {
              data.onSelectAnnotation(annotation.cid, false);
            }}
            sequenceId={data.sequenceId}
            sequenceStartIndex={data.sequenceStartIndex}
            sequenceLength={data.sequenceLength}
            isSingleStranded={data.isSingleStranded}
            allAnnotationTypes={data.allAnnotationTypes}
            allAnnotationFieldKeys={data.allCustomFieldKeys}
            isReadOnly={!data.canEditSequenceFeatures || !annotation.editable}
          />
        }
        contextMenuOptions={contextMenuOptions}
      />
    </div>
  );
}

export default function AnnotationPanelContent({
  sortedAnnotationsData,
  canEditSequenceFeatures,
  sequenceId,
  sequenceStartIndex,
  sequenceLength,
  isSingleStranded,
  hasDuplicateAnnotations,
  onToggleVisibility,
  onAnnotationHover,
  onSelectAnnotation,
  onSaveAnnotation,
  onClickAutoAnnotate,
  onDeleteAnnotation,
  onOpenCleanDuplicates,
  openExpandedAnnotations,
  className,
}: AnnotationPanelContentProps): JSX.Element {
  const {isCreateActive} = useAnnotationPanelUIBySequence(sequenceId);
  const dispatch = useDispatch<BenchlingDispatch>();
  const currentUser = EntityStore.currentUser();
  const featureLibraryUrl = currentUser && `/${currentUser.url_identifier}/feature-libraries/`;
  const allAnnotationTypes = getUniqueNonEmptyAnnotationTypes(sortedAnnotationsData);
  const allCustomFieldKeys = [
    ...new Set(sortedAnnotationsData.flatMap((anno) => anno.custom_fields.map(({name}) => name))),
  ];
  const selectedAnnotationIndex = sortedAnnotationsData.findIndex((anno) => anno.selected);

  const listRef = useRef<VariableSizeList>(null);
  useEffect(() => {
    // react-window caches the row sizes. Whenever the selection changes we need to bust the cache for all
    // rows because the currently selected row and the previously selected row both changed size.
    listRef.current?.resetAfterIndex(0);
    if (selectedAnnotationIndex !== -1) {
      listRef.current?.scrollToItem(selectedAnnotationIndex, 'start');
    }
  }, [selectedAnnotationIndex]);

  function onClickCreateNew(): void {
    analytics.track('annotation', 'new_annotation_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequenceId,
    });
    // When opening the creation form, cancel and close any in progress edits of existing annotations
    if (selectedAnnotationIndex !== -1) {
      onSelectAnnotation(sortedAnnotationsData[selectedAnnotationIndex].cid, false);
    }
    dispatch(openCreateNewAnnotation(sequenceId));
  }

  function getAnnotationPanelRowHeight(index: number): number {
    if (!sortedAnnotationsData[index]?.selected) {
      return BASE_FEATURE_ROW_HEIGHT;
    }

    // Calculate an approximate size for the expanded row based on the size of the elements in the edit
    // form. This value is used to set the maxHeight for the expanded row. The main goal is to set the height
    // large enough to avoid a nested scrollbar when first viewing an expanded annotation.
    return (
      // Extra padding
      BASE_FEATURE_ROW_HEIGHT +
      EDIT_FORM_BASE_FIELD_HEIGHT * NUM_EDIT_FIELDS +
      (sortedAnnotationsData[index].notes ? NOTES_FIELD_HEIGHT : EMPTY_NOTES_FIELD_HEIGHT) +
      (CUSTOM_FIELD_BASE_HEIGHT +
        // Account for height of placeholder text when there are no custom fields
        CUSTOM_FIELD_ROW_HEIGHT * Math.max(sortedAnnotationsData[index].custom_fields.length, 1)) +
      (sortedAnnotationsData[index].editable ? EDIT_FORM_BUTTONS_HEIGHT : 0) +
      20
    );
  }

  return (
    <div className={className} {...getTestProps({component: 'AnnotationPanelContent', element: 'root'})}>
      <div>
        <div className={css(styles.buttonRow)}>
          <AnnotationTypeVisibilityDropdown
            annotations={sortedAnnotationsData}
            onToggleVisibility={onToggleVisibility}
            useCompactSize={true}
          />
          {canEditSequenceFeatures && (
            <Button
              className={css(styles.createNewButton)}
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MINI}
              onClick={onClickCreateNew}
              text="Create new"
              dataTestAttrs={{component: 'AnnotationPanelContent', element: 'createNewButton'}}
            />
          )}
        </div>
        <div className={css(styles.countRow)}>
          <p>{sortedAnnotationsData.length} total</p>
          <p>{sortedAnnotationsData.filter(({visible}) => !visible).length} hidden</p>
        </div>
      </div>
      <div className={css(styles.annotationRowsSection)}>
        <VariableSizeList
          ref={listRef}
          itemData={{
            sortedAnnotationsData,
            onAnnotationHover,
            onToggleVisibility,
            onSaveAnnotation,
            sequenceId,
            sequenceStartIndex,
            sequenceLength,
            isSingleStranded,
            canEditSequenceFeatures,
            onDeleteAnnotation,
            openExpandedAnnotations,
            allAnnotationTypes,
            allCustomFieldKeys,
            onSelectAnnotation,
          }}
          height={selectedAnnotationIndex === -1 ? 200 : 590}
          width="100%"
          itemCount={sortedAnnotationsData.length}
          overscanCount={20}
          itemSize={getAnnotationPanelRowHeight}
          estimatedItemSize={BASE_FEATURE_ROW_HEIGHT}
          itemKey={(index) => `annotationPanelRow-${sortedAnnotationsData[index].cid}`}
        >
          {AnnotationPanelRow}
        </VariableSizeList>
      </div>
      {isCreateActive && canEditSequenceFeatures && (
        <CreateEditAnnotationForm
          initialAnnotationData={null}
          onClickSave={(data) => {
            onSaveAnnotation(null, data);
          }}
          onClickCancel={() => {
            dispatch(cancelCreateNewAnnotation(sequenceId));
          }}
          sequenceId={sequenceId}
          sequenceStartIndex={sequenceStartIndex}
          sequenceLength={sequenceLength}
          isSingleStranded={isSingleStranded}
          allAnnotationTypes={allAnnotationTypes}
          allAnnotationFieldKeys={allCustomFieldKeys}
          isReadOnly={false}
        />
      )}
      {hasDuplicateAnnotations && canEditSequenceFeatures && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="Duplicate annotations found."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            rightAlign: true,
            actionText: 'Clean duplicates',
            actionOnClick: onOpenCleanDuplicates,
          }}
        />
      )}
      {(!isCreateActive || !canEditSequenceFeatures) && (
        <div>
          <Button
            className={css(styles.expandedViewButton)}
            buttonStyle={ButtonStyle.LINK}
            disabled={false}
            onClick={openExpandedAnnotations}
            text="Show expanded view"
            dataTestAttrs={{component: 'AnnotationPanelContent', element: 'expandedAnnotationsLink'}}
          />
          {canEditSequenceFeatures && (
            <div className={css(styles.buttonRow)}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.MINI}
                onClick={onClickAutoAnnotate}
                text="Auto-annotate"
                dataTestAttrs={{component: 'AnnotationPanelContent', element: 'autoAnnotateButton'}}
              />
              {isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}) && (
                <ButtonLink
                  buttonStyle={ButtonStyle.LINK}
                  href={featureLibraryUrl}
                  target="_blank"
                  text="Edit feature libraries"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
  },
  countRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: GRAY_DARKER,
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  annotationRowsSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  expandedViewButton: {
    width: '100%',
    padding: '8px 0',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  cleanDuplicatesButton: {
    marginLeft: '8px',
  },
  createNewButton: {
    marginTop: '2px',
  },
});
